import React from 'react';
import { Grid, Tooltip } from '@mui/material';
import { Controller } from 'react-hook-form';
import dayjs from 'dayjs';

import { EsgSingleSelectControlledField, TextField, EsgDatePickerField } from 'components';
import { getDateFormatset } from 'utils';
import { useNotify } from 'hooks';

import { ESTIMATE_TYPES_OPTIONS, SCOPES_OPTIONS } from './consts';
import { FormFieldsProps } from './types';
import { Notes } from './components';
import { fieldStyles } from '../styles';

export const FormFields: React.FC<FormFieldsProps> = ({
  control,
  watch,
  setValue,
  allowedValues,
  notesError,
  setNotesError,
  isNotesOnlyMode,
  availableUnitDescriptions,
}) => {
  const notify = useNotify();
  const watchedUtilityType = watch("utilityName");

  const dropdownOptions = {
    utilityTypes: allowedValues?.utilityTypes?.sort((a: any, b: any) =>
      a.name.localeCompare(b.name)).map((ut: any) => ({
        label: ut.name,
        value: ut.name,
      })) || [],

    estimateTypes: ESTIMATE_TYPES_OPTIONS || [],

    consumptionUnits: availableUnitDescriptions.map(currentUnit => ({
      label: `${currentUnit.unit} (${currentUnit.description})`,
      value: currentUnit.unit,
    })) || [],

    scopes: SCOPES_OPTIONS || [],
  };

  const handleUtilityTypeChange = (value: string | number) => {
    const selectedUtilityType = allowedValues?.utilityTypes?.find((ut: any) => ut.name === value);
    const ghgEmission = selectedUtilityType?.ghgEmission;
    const scopeValue = ghgEmission === "No Scope" ? "NO_SCOPE" : ghgEmission;

    setValue("utilityName", value as string);
    setValue("scope", scopeValue);
  };

  if (isNotesOnlyMode) {
    return <Notes control={control} notesError={notesError} setNotesError={setNotesError} />;
  };

  return (
    <>
      {/* Estimate Name */}
      <Grid item xs={6}>
        <Controller
          name="estimateName"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              size="small"
              sx={fieldStyles.textField}
              label="Estimate Name"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </Grid>

      {/* Utility Type */}
      <Grid item xs={6}>
        <EsgSingleSelectControlledField
          label="Utility Type"
          selectedValue={watchedUtilityType}
          setSelectedValue={handleUtilityTypeChange}
          options={dropdownOptions.utilityTypes}
          placeholder="Select Utility Type"
          width="100%"
        />
      </Grid>

      {/* Estimate Type */}
      <Grid item xs={6}>
        <EsgSingleSelectControlledField
          label="Estimate Type"
          selectedValue={watch("estimateType")}
          setSelectedValue={(value) => setValue("estimateType", value as string)}
          options={dropdownOptions.estimateTypes}
          disabled={!watchedUtilityType}
          placeholder="Select Estimate Type"
          width="100%"
        />
      </Grid>

      {/* Estimation Value */}
      <Grid item xs={6}>
        <Controller
          name="estimateHeadCount"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip
              title="E.g. Headcount of 15"
              placement="top"
              arrow
            >
              <TextField
                {...field}
                size="small"
                sx={fieldStyles.textField}
                label="Estimation Value"
                error={!!error}
                helperText={error?.message}
              />
            </Tooltip>
          )}
        />
      </Grid>

      {/* Estimated Usage per Day */}
      <Grid item xs={6}>
        <Controller
          name="estimateUsage"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip
              title="E.g. Electricity usage of 12.5 kWh per headcount per day"
              placement="top"
              arrow
            >
              <TextField
                {...field}
                size="small"
                sx={fieldStyles.textField}
                label="Estimated Usage per Day"
                error={!!error}
                helperText={error?.message}
              />
            </Tooltip>
          )}
        />
      </Grid>

      {/* Consumption Unit */}
      <Grid item xs={6}>
        <EsgSingleSelectControlledField
          label="Consumption Unit"
          selectedValue={watch("estimateUsageUnit")}
          setSelectedValue={(value) => setValue("estimateUsageUnit", value as string)}
          options={dropdownOptions.consumptionUnits}
          disabled={!watchedUtilityType}
          placeholder="Select Consumption Unit"
          width="100%"
        />
      </Grid>

      {/* Start Date */}
      <Grid item xs={6}>
        <Controller
          name="estimateStartDate"
          control={control}
          render={({ field }) => (
            <EsgDatePickerField
              label="Start Date"
              value={field.value ? dayjs.utc(field.value) : null}
              onChange={(date) => field.onChange(date?.format("YYYY-MM-DDT00:00:00[Z]"))}
              format={getDateFormatset()}
              clearDate={false}
              width="100%"
              placeholdervalue={getDateFormatset()}
            />
          )}
        />
      </Grid>

      {/* End Date */}
      <Grid item xs={6}>
        <Controller
          name="estimateEndDate"
          control={control}
          render={({ field }) =>
            <EsgDatePickerField
              label="End Date"
              value={field.value ? dayjs.utc(field.value) : null}
              onChange={(date) => {
                const givenDate = date;
                const startDate = watch("estimateStartDate");

                if (givenDate?.isBefore(startDate)) {
                  notify.error("A parameter based estimate cannot have an end date before the start date");
                } else {
                  field.onChange(date?.format("YYYY-MM-DDT00:00:00[Z]"));
                }
              }}
              format={getDateFormatset()}
              clearDate={false}
              width="100%"
              placeholdervalue={getDateFormatset()}
            />
          }
        />
      </Grid>

      {/* Scope */}
      <Grid item xs={6}>
        <EsgSingleSelectControlledField
          label="Scope"
          selectedValue={watch("scope")}
          setSelectedValue={(value) => setValue("scope", value as string)}
          options={dropdownOptions.scopes}
          disabled={!watchedUtilityType}
          placeholder="Select Scope"
          width="100%"
        />
      </Grid>

      {/* Estimate Source */}
      <Grid item xs={6}>
        <Controller
          name="estimateSource"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              size="small"
              sx={fieldStyles.textField}
              label="Estimate Source"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </Grid>
    </>
  );
};
